import { z } from 'zod';

import { WEEKLY_MENU_STATUSES } from '../enums';
import { PaginationSchema } from './pagination';

/**
 * The request contracts of `uml/18-api-specification.md` §8.1.
 *
 * They live in `shared` and not in the API because the web app parses the same
 * shapes before it sends them, so a field renamed here breaks both builds in
 * one commit. That is cheaper than a runtime 422 nobody sees until the demo.
 *
 * **These validate SHAPE only.** Not one of them knows whether a menu is
 * frozen, whether the week is already published, whether the week starts on a
 * Monday (D-006), or whether the caller may publish it. Those are rules, they
 * live in `MenuService`, and a Zod schema that started enforcing them would be
 * a second place to look for RG59 - and the two copies would drift.
 *
 * The line between the two: a schema refuses what could never be valid for any
 * menu in any state. A service refuses what is invalid for *this* menu *now*.
 */

/**
 * A DATE column: `2026-09-21`. No time, no offset.
 *
 * The `refine` is what rejects 2026-02-30. The regex alone accepts it, and
 * `new Date('2026-02-30')` rolls it forward to 2 March rather than failing -
 * so a manager's typo would silently create a menu for the wrong week.
 */
const CalendarDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, 'Not a real calendar date');

/**
 * A `TIME(6)` column read as a wall clock: `12:00`, or `12:00:30`.
 *
 * No date and no offset, deliberately - `meals.service_start` has none either.
 * Turning one into an instant needs `days.date` and the zone, which is
 * `restaurant/domain/service-window.ts` and RG52, not this file.
 */
const ClockTime = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, 'Expected HH:MM or HH:MM:SS');

/** An instant. `registration_cutoff` is TIMESTAMPTZ, so this one carries an offset. */
const Instant = z.string().datetime({ offset: true });

/** Minutes since midnight, for ordering two wall clocks that have no date. */
const minutesOf = (clock: string): number => {
  const [hours, minutes] = clock.split(':');
  return Number(hours) * 60 + Number(minutes);
};

/** `POST /menus`. RG49. The Monday check is D-006, in MenuService. */
export const CreateMenuSchema = z.object({
  weekStartDate: CalendarDate,
});
export type CreateMenuInput = z.infer<typeof CreateMenuSchema>;

/** `POST /menus/duplicate`. RG61. */
export const DuplicateMenuSchema = z.object({
  fromWeek: CalendarDate,
  toWeek: CalendarDate,
});
export type DuplicateMenuInput = z.infer<typeof DuplicateMenuSchema>;

/** `PATCH /menus/:id/meals/:mealId`. RG52, RG53, RG64. */
export const SetMealWindowSchema = z
  .object({
    serviceStart: ClockTime,
    serviceEnd: ClockTime,
    registrationCutoff: Instant,
  })
  // RG52: a window has a start and an end, so an end at or before the start is
  // not a window. Shape, not rule - it is true of every meal that can exist.
  .refine((data) => minutesOf(data.serviceEnd) > minutesOf(data.serviceStart), {
    message: 'serviceEnd must be after serviceStart',
    path: ['serviceEnd'],
  });
export type SetMealWindowInput = z.infer<typeof SetMealWindowSchema>;

/** `PUT /menus/:id/meals/:mealId/options`. RG51. */
export const SetOptionsSchema = z.object({
  /** RG51: "one or several". An empty list is not a meal. */
  options: z
    .array(
      z.object({
        name: z.string().trim().min(1).max(255),
        description: z.string().trim().max(2000).optional(),
      }),
    )
    .min(1)
    .max(20),
});
export type SetOptionsInput = z.infer<typeof SetOptionsSchema>;

/** `POST /menus/no-service`. RG144. */
export const PublishNoServiceSchema = z.object({
  weekStartDate: CalendarDate,
  /** The message IS the feature: it separates closed from unprepared. */
  message: z.string().trim().min(1).max(500),
});
export type PublishNoServiceInput = z.infer<typeof PublishNoServiceSchema>;

/** `PATCH /menus/:id/options/:optionId`. RG57. */
export const EditOptionSchema = z
  .object({
    name: z.string().trim().min(1).max(255).optional(),
    description: z.string().trim().max(2000).optional(),
    /** `menu_versions.change_summary` is NOT NULL, and rightly so. */
    changeSummary: z.string().trim().min(1).max(500),
  })
  .refine((data) => data.name !== undefined || data.description !== undefined, {
    message: 'An edit must change the name or the description',
    path: ['name'],
  });
export type EditOptionInput = z.infer<typeof EditOptionSchema>;

/**
 * `GET /menus`. Doc 18 §2.6 paging, plus the one filter §8.1 names.
 *
 * Extends the shared `PaginationSchema` rather than restating it, so `page`,
 * `pageSize` and the 100 cap have one definition for all 155 endpoints.
 */
export const ListMenusQuerySchema = PaginationSchema.extend({
  status: z.enum(WEEKLY_MENU_STATUSES).optional(),
});
export type ListMenusQuery = z.infer<typeof ListMenusQuerySchema>;

/**
 * `POST /meals/:id/check-ins`. Client review 2: the student picks the dish they
 * took during service; there is no code.
 *
 * A missing or non-integer id is a **400 from Zod**. A dish of another meal is a
 * 422 from the service (RG68) - the shape is right, the rule says no.
 */
export const CheckInSchema = z.object({
  mealOptionId: z.number().int().positive(),
});
export type CheckInInput = z.infer<typeof CheckInSchema>;

/**
 * `GET /restaurant/dishes`. RG156. Every field optional: no search lists every
 * dish, and the range defaults to the current academic year (D-029).
 *
 * The dates are calendar days, `YYYY-MM-DD`, because `days.date` is a DATE and
 * a time of day here would only invite an offset bug.
 */
const CALENDAR_DAY = z
  .string()
  .regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/)
  .transform((day) => new Date(`${day}T00:00:00.000Z`))
  .refine((day) => !Number.isNaN(day.getTime()), 'Not a calendar day');

export const DishHistoryQuerySchema = PaginationSchema.extend({
  search: z.string().trim().max(100).optional(),
  from: CALENDAR_DAY.optional(),
  to: CALENDAR_DAY.optional(),
});
export type DishHistoryQuery = z.infer<typeof DishHistoryQuerySchema>;

/**
 * `POST /meals/:id/ratings`. RG69: a mandatory score of one to five stars and
 * an optional written review.
 *
 * A score of 0, 6 or 4.5 is a **400 from Zod**, not a 422 - the shape is wrong
 * and no rule was consulted. `int()` is what refuses 4.5; RG69 says stars.
 */
export const RateMealSchema = z.object({
  // RG155: a student's check-in already names the dish, and the server ignores
  // this for them. Only a RATE_WITHOUT_CHECK_IN holder (RG67's exemption) has
  // no check-in to read it from, so for them the service requires it.
  mealOptionId: z.number().int().positive().optional(),
  score: z.number().int().min(1).max(5),
  review: z.string().trim().max(2000).optional(),
});
export type RateMealInput = z.infer<typeof RateMealSchema>;

/**
 * `PATCH /ratings/:id`. RG71. Both fields optional, at least one required - a
 * PATCH that changes nothing is a mistake, not a no-op.
 *
 * `mealOptionId` is deliberately absent: doc 18 §8.3 gives PATCH score and
 * review only. Changing which dish you ate is a different rating.
 */
export const EditRatingSchema = z
  .object({
    score: z.number().int().min(1).max(5).optional(),
    review: z.string().trim().max(2000).nullable().optional(),
  })
  .refine((data) => data.score !== undefined || data.review !== undefined, {
    message: 'An edit must change the score or the review',
    path: ['score'],
  });
export type EditRatingInput = z.infer<typeof EditRatingSchema>;

/**
 * RES-17 / RG78, and decision D-020. Shared so the manager's upload screen
 * refuses a fifth file or a 6 MB photo before the request, with the same
 * numbers the API enforces - a DTO change breaks both builds at once.
 */
export const MAX_PHOTOS_PER_MEAL = 4;
export const MEAL_PHOTO_MAX_BYTES = 5 * 1024 * 1024;
export const MEAL_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/**
 * RES-16 / RG73, D-022. Shared so the comment box counts down with the number
 * the API enforces.
 */
export const MEAL_COMMENT_MAX_LENGTH = 500;

/**
 * `POST /meals/:id/comments` and `PATCH /comments/:id`. RG73, D-022.
 *
 * Trimmed first, then measured: `trim()` runs before `min` and `max` in the
 * chain, so a trailing space from a phone keyboard neither costs a letter nor
 * turns "   " into a comment. Both refusals are a **400 from Zod** - the shape
 * is wrong and no rule was consulted.
 *
 * One schema for both rows, deliberately. An edit replaces the body with
 * another body, so a second schema would be the same three checks under a
 * different name, and the two would drift.
 */
export const CommentMealSchema = z.object({
  body: z.string().trim().min(1).max(MEAL_COMMENT_MAX_LENGTH),
});
export type CommentMealInput = z.infer<typeof CommentMealSchema>;
